import React, { useEffect, useState } from 'react';
import HeroCarousel from './home/HeroCarousel';
import CategoryTiles from './home/CategoryTiles';
import ProductCard from './ProductCard';
import HotmailDeliveryNotice from './HotmailDeliveryNotice';

/**
 * Dynamic page renderer driven by `page_sections` rows from the page builder.
 *
 * Usage:
 *   <PageRenderer slug="home" fallback={<LegacyHomeLayout />} />
 *
 * Behaviour:
 *   - Fetches /api/page-builder/pages/:slug
 *   - If sections exist → renders them in order
 *   - If page has zero sections OR fetch fails → renders the `fallback`
 *     prop (the existing hardcoded layout)
 *
 * This is the bridge that lets us ship the page builder without breaking
 * any existing customer-facing page. As long as a page has no sections in
 * the DB, customers see exactly what they see today.
 */
export default function PageRenderer({ slug, fallback }) {
  const [sections, setSections] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // When the page renders inside the page builder iframe (?builder=1), fetch
    // the admin preview endpoint instead — it returns DRAFT props so admins
    // can see unpublished edits before clicking Publish. Customers (no
    // builder=1, no admin token) always hit the public endpoint and see live.
    const qs = new URLSearchParams(window.location.search);
    const isBuilder = qs.get('builder') === '1';
    const token = isBuilder ? localStorage.getItem('admin_token') : null;
    const url = isBuilder && token
      ? `/api/page-builder/admin/preview/${encodeURIComponent(slug)}`
      : `/api/page-builder/pages/${encodeURIComponent(slug)}`;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        setSections(Array.isArray(data?.sections) ? data.sections : []);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [slug]);

  // Loading state — show fallback so the page doesn't flash blank. The
  // fallback is the current hardcoded layout; once builder data lands,
  // we swap to that.
  if (sections === null && !error) return fallback || null;

  // Error or empty-sections → use fallback. This is the safety net that
  // makes the rollout zero-risk.
  if (error || !sections.length) return fallback || null;

  return (
    <>
      {sections.map(s => <SectionRenderer key={s.id} section={s} />)}
    </>
  );
}

function SectionRenderer({ section }) {
  const { type, props = {}, props_mobile, variants } = section;
  // A/B variant pick: sticky per-visitor via cookie 'sg_vid'. Weighted random
  // chooses control (variants empty or weight applies) vs alternatives.
  const chosen = useVariantPick(section.id, variants);
  const baseProps = chosen ? chosen.props : props;
  // Apply mobile overrides on viewports ≤ 768px. Re-evaluates on resize so a
  // designer dragging the device toggle in the page builder iframe sees both
  // states without a refresh.
  const isMobile = useIsMobile();
  const effectiveProps = (isMobile && props_mobile) ? { ...baseProps, ...props_mobile } : baseProps;
  // Fire one impression per page-load per section (not per re-render).
  // Also stash the chosen variant in sessionStorage so abEvents.recordConversion
  // can attribute later CTA clicks to the right bucket.
  useEffect(() => {
    if (!Array.isArray(variants) || !variants.length) return;
    const variantId = chosen ? chosen.id : null;
    try {
      const map = JSON.parse(sessionStorage.getItem('sg_ab_variants') || '{}');
      map[section.id] = variantId;
      sessionStorage.setItem('sg_ab_variants', JSON.stringify(map));
    } catch (_) {}
    // First section seen this session — credit it on post-checkout conversions.
    try {
      if (!sessionStorage.getItem('sg_ab_first_section')) {
        sessionStorage.setItem('sg_ab_first_section', String(section.id));
      }
    } catch (_) {}
    fetch('/api/page-builder/event', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section_id: section.id, variant_id: variantId, type: 'impression' }),
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id, chosen?.id]);
  const inner = renderSectionInner(type, effectiveProps);
  if (!inner) return null;
  const p = effectiveProps;
  // Wrap every section so the layout controls (margin, container width) apply
  // uniformly without each renderer having to know about them.
  const containerStyle = (() => {
    if (p.container === 'full')   return { maxWidth: 'none', padding: 0 };
    if (p.container === 'narrow') return { maxWidth: 800, margin: '0 auto', padding: '0 16px' };
    return {};
  })();
  const style = {
    marginTop: p.margin_top != null ? p.margin_top : undefined,
    marginBottom: p.margin_bottom != null ? p.margin_bottom : undefined,
    ...containerStyle,
  };
  // data-section-id is read by abEvents.recordConversionFromEvent when a CTA
  // inside this section fires a conversion.
  return <div style={style} data-section-id={section.id}>{inner}</div>;
}

// A/B variant chooser. Sticky per-visitor via 'sg_vid' cookie hashed against
// the section id so the same visitor consistently sees the same variant on
// repeat visits — crucial for clean conversion attribution.
function useVariantPick(sectionId, variants) {
  if (!Array.isArray(variants) || !variants.length) return null;
  const vid = getOrCreateVid();
  // Hash visitor id + section id deterministically into [0, totalWeight).
  const totalWeight = variants.reduce((a, v) => a + Math.max(1, Number(v.weight) || 1), 1);
  let h = 5381;
  const s = vid + ':' + sectionId;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) & 0xffffffff;
  let pick = Math.abs(h) % totalWeight;
  // First slot = control (returns null → renders default props).
  if (pick < 1) return null;
  pick -= 1;
  for (const v of variants) {
    const w = Math.max(1, Number(v.weight) || 1);
    if (pick < w) return v;
    pick -= w;
  }
  return null;
}

function getOrCreateVid() {
  if (typeof document === 'undefined') return 'ssr';
  const m = document.cookie.match(/(?:^|;\s*)sg_vid=([^;]+)/);
  if (m) return decodeURIComponent(m[1]);
  const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
  document.cookie = `sg_vid=${id}; path=/; max-age=${60 * 60 * 24 * 365}`;
  return id;
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return m;
}

function renderSectionInner(type, props) {
  switch (type) {
    case 'hero_carousel': {
      // Map page-builder field names → HeroCarousel field names. If admin has
      // configured slides, use them; if empty, fall through to HeroCarousel's
      // own defaults / Pokemon TCG fallback so the hero is never blank.
      const mapped = (props.slides || []).map(s => ({
        title: s.headline || s.title,
        subtitle: s.subhead || s.subtitle,
        image: s.image,
        cta: s.cta,
        href: s.href,
        bg_color: s.bg_color,
        text_color: s.text_color,
        button_bg: s.button_bg,
        button_text: s.button_text,
        overlay_opacity: s.overlay_opacity,
      }));
      return <HeroCarousel slides={mapped.length ? mapped : undefined}
                           autoplay={props.autoplay}
                           intervalMs={props.interval_ms} />;
    };

    case 'category_tiles':
      // For Phase 1 we re-use the existing CategoryTiles component as-is.
      // Phase 2 wires per-tile image overrides from props.tiles.
      return <CategoryTiles />;

    case 'featured_products':
      return <FeaturedProductsSection props={props} />;

    case 'image_text':
      return <ImageTextSection props={props} />;

    case 'rich_text':
      return (
        <section style={{ padding: '32px 16px', textAlign: props.align || 'left', maxWidth: 900, margin: '0 auto' }}>
          {props.heading && <h2 style={{ margin: '0 0 12px' }}>{props.heading}</h2>}
          {props.body && <div style={{ lineHeight: 1.6, color: '#444', whiteSpace: 'pre-wrap' }}>{props.body}</div>}
          {props.cta && props.href && (
            <a href={props.href} className="btn btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>{props.cta}</a>
          )}
        </section>
      );

    case 'spacer':
      return <div style={{ height: Number(props.height) || 48 }} aria-hidden="true" />;

    case 'video': {
      // Convert any youtube/vimeo URL to an embed URL.
      const embed = toEmbedUrl(props.url);
      if (!embed) return null;
      return (
        <section style={{ padding: '32px 16px', maxWidth: 960, margin: '0 auto' }}>
          {props.heading && <h2 style={{ margin: '0 0 16px', textAlign: 'center' }}>{props.heading}</h2>}
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 12 }}>
            <iframe src={embed} title={props.heading || 'Video'}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen />
          </div>
        </section>
      );
    }

    case 'testimonials': {
      const items = Array.isArray(props.items) ? props.items : [];
      if (!items.length) return null;
      return (
        <section style={{ padding: '48px 16px', background: '#f8f8f9' }}>
          {props.heading && <h2 style={{ textAlign: 'center', margin: '0 0 24px' }}>{props.heading}</h2>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, maxWidth: 1100, margin: '0 auto' }}>
            {items.map((t, i) => (
              <blockquote key={i} style={{ background: '#fff', padding: 24, borderRadius: 8, margin: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 14, lineHeight: 1.6, color: '#333' }}>"{t.quote}"</div>
                <footer style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
                  — {t.author}{t.role ? `, ${t.role}` : ''}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      );
    }

    case 'faq': {
      const items = Array.isArray(props.items) ? props.items : [];
      return (
        <section style={{ padding: '48px 16px', maxWidth: 800, margin: '0 auto' }}>
          {props.heading && <h2 style={{ margin: '0 0 24px' }}>{props.heading}</h2>}
          {items.map((it, i) => (
            <details key={i} style={{ borderBottom: '1px solid #eee', padding: '12px 0' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>{it.question}</summary>
              <div style={{ marginTop: 8, color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{it.answer}</div>
            </details>
          ))}
        </section>
      );
    }

    case 'newsletter':
      return <NewsletterSection props={props} />;

    case 'custom_html':
      // eslint-disable-next-line react/no-danger
      return <section dangerouslySetInnerHTML={{ __html: props.html || '' }} />;

    default:
      // Unknown section type — log + render nothing rather than crash.
      // eslint-disable-next-line no-console
      console.warn(`PageRenderer: unknown section type "${type}"`);
      return null;
  }
}

function FeaturedProductsSection({ props }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const limit = Number(props.limit) || 8;
    let url = `/api/products?limit=${limit}`;
    switch (props.feed) {
      case 'newest':         url += '&sort=newest'; break;
      case 'trending':       url += '&sort=trending'; break;
      case 'on-sale':        url += '&on_sale=true'; break;
      case 'manual':         url = `/api/products?ids=${(props.product_ids || []).join(',')}`; break;
      case 'featured-flag':
      default:               url += '&featured=true';
    }
    fetch(url).then(r => r.ok ? r.json() : []).then(setProducts).catch(() => {});
  }, [props.feed, props.limit, JSON.stringify(props.product_ids || [])]);

  if (!products.length) return null;
  return (
    <section className="featured-section">
      <div className="section-header">
        <h2>{props.heading || 'Featured Products'}</h2>
      </div>
      <div className="grid">
        {products.map(p => <ProductCard key={p.id} product={p} />)}
      </div>
    </section>
  );
}

function toEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const v = url.match(/vimeo\.com\/(\d+)/);
  if (v) return `https://player.vimeo.com/video/${v[1]}`;
  return url; // assume already an embed URL
}

function NewsletterSection({ props }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault();
    setErr('');
    try {
      const r = await fetch('/api/newsletter/subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'page_builder_section' }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'failed');
      setDone(true);
    } catch (e) { setErr(e.message); }
  }
  return (
    <section style={{ padding: '48px 16px', textAlign: 'center', background: '#0d2540', color: '#fff' }}>
      {props.heading && <h2 style={{ margin: '0 0 8px', color: '#fff' }}>{props.heading}</h2>}
      {props.subhead && <p style={{ margin: '0 0 20px', color: 'rgba(255,255,255,0.85)' }}>{props.subhead}</p>}
      {done ? (
        <div style={{ fontSize: 15 }}>Thanks — you're on the list.</div>
      ) : (
        <>
          <form onSubmit={submit} style={{ display: 'inline-flex', gap: 8, maxWidth: 480, width: '100%' }}>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ flex: 1, padding: '10px 12px', borderRadius: 6, border: 'none', fontSize: 14 }} />
            <button type="submit" className="btn btn-primary" style={{ padding: '10px 20px' }}>Subscribe</button>
          </form>
          <div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'left' }}>
            <HotmailDeliveryNotice email={email} style={{ marginTop: 10 }} />
          </div>
        </>
      )}
      {err && <div style={{ marginTop: 12, color: '#ff8080', fontSize: 13 }}>{err}</div>}
    </section>
  );
}

function ImageTextSection({ props }) {
  const imageOnRight = (props.image_side || 'right') === 'right';
  return (
    <section style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 32,
      alignItems: 'center',
      margin: '48px 0',
      padding: '32px 16px',
      background: props.bg_color || 'transparent',
      color: props.text_color || undefined,
    }} className="image-text-section">
      {!imageOnRight && props.image && (
        <img src={props.image} alt="" style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }} loading="lazy" />
      )}
      <div>
        {props.heading && <h2 style={{ margin: '0 0 12px', color: props.text_color || undefined }}>{props.heading}</h2>}
        {props.body && <p style={{ lineHeight: 1.6, color: props.text_color || '#555', margin: '0 0 16px' }}>{props.body}</p>}
        {props.cta && props.href && (
          <a href={props.href} className="btn btn-primary"
            style={{
              display: 'inline-block',
              background: props.button_bg || undefined,
              color: props.button_text || undefined,
              borderColor: props.button_bg || undefined,
            }}>
            {props.cta}
          </a>
        )}
      </div>
      {imageOnRight && props.image && (
        <img src={props.image} alt="" style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }} loading="lazy" />
      )}
    </section>
  );
}
